import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paperclip,
  Upload,
  Link as LinkIcon,
  File,
  Image,
  Music,
  Video,
  FileText,
  Trash2,
  ExternalLink,
  X,
} from 'lucide-react';
import { Card, Button, Input, Modal } from '../ui';
import { attachmentsApi, ProjectAttachmentDto } from '../../services';
import './AttachmentManager.css';

interface AttachmentManagerProps {
  projectId: string;
  attachments: ProjectAttachmentDto[];
  onAttachmentAdded?: (attachment: ProjectAttachmentDto) => void;
  onAttachmentRemoved?: (attachmentId: string) => void;
  readOnly?: boolean;
}

const getFileIcon = (mimeType?: string, fileName?: string) => {
  if (!mimeType && fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || ''))
      return <Image size={20} />;
    if (['mp3', 'wav', 'flac', 'aac', 'm4a'].includes(ext || ''))
      return <Music size={20} />;
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext || ''))
      return <Video size={20} />;
    if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext || ''))
      return <FileText size={20} />;
  }

  if (mimeType?.startsWith('image/')) return <Image size={20} />;
  if (mimeType?.startsWith('audio/')) return <Music size={20} />;
  if (mimeType?.startsWith('video/')) return <Video size={20} />;
  if (mimeType?.includes('pdf') || mimeType?.includes('document'))
    return <FileText size={20} />;

  return <File size={20} />;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Allowed file types for artist workflow attachments
const ALLOWED_FILE_TYPES = {
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
  // Audio
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/flac': ['.flac'],
  'audio/aac': ['.aac'],
  'audio/x-m4a': ['.m4a'],
  'audio/ogg': ['.ogg'],
  // Video
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
  // Documents
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const ACCEPTED_EXTENSIONS = Object.values(ALLOWED_FILE_TYPES).flat().join(',');
const ACCEPTED_MIME_TYPES = Object.keys(ALLOWED_FILE_TYPES).join(',');
const MAX_FILE_SIZE_MB = 10;

export const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  projectId,
  attachments,
  onAttachmentAdded,
  onAttachmentRemoved,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`;
    }

    // Check file type
    const isAllowedMime = Object.keys(ALLOWED_FILE_TYPES).includes(file.type);
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isAllowedExt = ACCEPTED_EXTENSIONS.includes(extension);

    if (!isAllowedMime && !isAllowedExt) {
      return `File type not allowed: ${file.name}. Allowed: images, audio, video, PDF, text files.`;
    }

    return null;
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      setIsUploading(true);
      setUploadError(null);

      try {
        for (const file of Array.from(files)) {
          // Validate file before upload
          const validationError = validateFile(file);
          if (validationError) {
            setUploadError(validationError);
            continue;
          }

          const attachmentType = file.type.startsWith('image/')
            ? 'Image'
            : file.type.startsWith('audio/')
              ? 'Audio'
              : file.type.startsWith('video/')
                ? 'Video'
                : 'Document';

          const attachment = await attachmentsApi.uploadFile(
            projectId,
            file,
            attachmentType
          );
          onAttachmentAdded?.(attachment);
        }
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : 'Failed to upload file'
        );
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [projectId, onAttachmentAdded, validateFile]
  );

  const handleAddLink = useCallback(async () => {
    if (!linkUrl.trim()) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const attachment = await attachmentsApi.addLink(projectId, {
        url: linkUrl.trim(),
        title: linkTitle.trim() || undefined,
      });
      onAttachmentAdded?.(attachment);
      setShowLinkModal(false);
      setLinkUrl('');
      setLinkTitle('');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to add link');
    } finally {
      setIsUploading(false);
    }
  }, [projectId, linkUrl, linkTitle, onAttachmentAdded]);

  const handleRemove = useCallback(
    async (attachmentId: string) => {
      if (!window.confirm('Are you sure you want to remove this attachment?'))
        return;

      try {
        await attachmentsApi.remove(projectId, attachmentId);
        onAttachmentRemoved?.(attachmentId);
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : 'Failed to remove attachment'
        );
      }
    },
    [projectId, onAttachmentRemoved]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (readOnly) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      setIsUploading(true);
      setUploadError(null);

      try {
        for (const file of files) {
          // Validate file before upload
          const validationError = validateFile(file);
          if (validationError) {
            setUploadError(validationError);
            continue;
          }

          const attachmentType = file.type.startsWith('image/')
            ? 'Image'
            : file.type.startsWith('audio/')
              ? 'Audio'
              : file.type.startsWith('video/')
                ? 'Video'
                : 'Document';

          const attachment = await attachmentsApi.uploadFile(
            projectId,
            file,
            attachmentType
          );
          onAttachmentAdded?.(attachment);
        }
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : 'Failed to upload file'
        );
      } finally {
        setIsUploading(false);
      }
    },
    [projectId, onAttachmentAdded, readOnly, validateFile]
  );

  const fileAttachments = attachments.filter(a => a.attachmentType !== 'Link');
  const linkAttachments = attachments.filter(a => a.attachmentType === 'Link');

  return (
    <Card className="attachment-manager">
      <div className="attachment-manager__header">
        <h3 className="attachment-manager__title">
          <Paperclip size={18} />
          Attachments
          <span className="attachment-manager__count">
            ({attachments.length})
          </span>
        </h3>
      </div>

      {/* Upload Area */}
      {!readOnly && (
        <div
          className={`attachment-manager__upload ${isUploading ? 'attachment-manager__upload--uploading' : ''}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_MIME_TYPES}
            onChange={handleFileSelect}
            className="attachment-manager__file-input"
            disabled={isUploading}
          />
          <div className="attachment-manager__upload-content">
            <Upload size={24} />
            <p>
              {isUploading
                ? 'Uploading...'
                : 'Drag files here or click to upload'}
            </p>
            <span className="attachment-manager__upload-hint">
              Images, Audio, Video, PDF, Text (max {MAX_FILE_SIZE_MB}MB)
            </span>
          </div>
        </div>
      )}

      {/* Add Link Button */}
      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          className="attachment-manager__add-link"
          onClick={() => setShowLinkModal(true)}
        >
          <LinkIcon size={14} />
          Add Link
        </Button>
      )}

      {/* Error Display */}
      {uploadError && (
        <div className="attachment-manager__error">
          <X size={14} />
          {uploadError}
        </div>
      )}

      {/* File Attachments */}
      {fileAttachments.length > 0 && (
        <div className="attachment-manager__section">
          <h4 className="attachment-manager__section-title">Files</h4>
          <div className="attachment-manager__list">
            {fileAttachments.map(attachment => (
              <div key={attachment.id} className="attachment-manager__item">
                <div className="attachment-manager__item-icon">
                  {getFileIcon(attachment.mimeType, attachment.fileName)}
                </div>
                <div className="attachment-manager__item-info">
                  <a
                    href={attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-manager__item-name"
                  >
                    {attachment.fileName}
                    <ExternalLink size={12} />
                  </a>
                  <span className="attachment-manager__item-meta">
                    {attachment.attachmentType}
                    {attachment.fileSize && ` • ${formatFileSize(attachment.fileSize)}`}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    className="attachment-manager__remove"
                    onClick={() => handleRemove(attachment.id)}
                    title="Remove attachment"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Attachments */}
      {linkAttachments.length > 0 && (
        <div className="attachment-manager__section">
          <h4 className="attachment-manager__section-title">Links</h4>
          <div className="attachment-manager__list">
            {linkAttachments.map(attachment => (
              <div key={attachment.id} className="attachment-manager__item">
                <div className="attachment-manager__item-icon">
                  <LinkIcon size={20} />
                </div>
                <div className="attachment-manager__item-info">
                  <a
                    href={attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-manager__item-name"
                  >
                    {attachment.fileName || attachment.fileUrl}
                    <ExternalLink size={12} />
                  </a>
                  <span className="attachment-manager__item-meta">
                    {new Date(attachment.uploadedAt).toLocaleDateString()}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    className="attachment-manager__remove"
                    onClick={() => handleRemove(attachment.id)}
                    title="Remove link"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {attachments.length === 0 && (
        <p className="attachment-manager__empty">
          No attachments yet.{' '}
          {!readOnly && 'Upload files or add links to get started.'}
        </p>
      )}

      {/* Add Link Modal */}
      <Modal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        title={t('attachments.addLink')}
      >
        <div className="attachment-manager__link-form">
          <Input
            label={t('attachments.linkUrl')}
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            placeholder={t('attachments.linkUrlPlaceholder')}
            required
          />
          <Input
            label={t('attachments.linkTitle')}
            type="text"
            value={linkTitle}
            onChange={e => setLinkTitle(e.target.value)}
            placeholder={t('attachments.linkTitlePlaceholder')}
          />
          <div className="attachment-manager__link-actions">
            <Button variant="ghost" onClick={() => setShowLinkModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddLink}
              disabled={!linkUrl.trim() || isUploading}
            >
              {isUploading ? t('common.loading') : t('attachments.addLink')}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default AttachmentManager;
